
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PaymentsTable } from '@/components/courier/payments/PaymentsTable';
import { PaymentStatCards } from '@/components/courier/payments/PaymentStatCards';
import { PaymentFilters } from '@/components/courier/payments/PaymentFilters';
import { useCourierPaymentsData } from '@/hooks/useCourierPaymentsData';
import { Payment } from '@/services/types';
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const CourierPayments: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const courierPaymentsData = useCourierPaymentsData();

  const filteredPayments = courierPaymentsData.payments?.filter((payment: Payment) => {
    if (statusFilter === 'all') return true;
    return payment.status === statusFilter;
  }) || [];

  const handleBackToDashboard = () => {
    navigate('/courier');
  };

  if (courierPaymentsData.error) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={handleBackToDashboard}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Назад к главной
          </Button>
          
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink onClick={handleBackToDashboard} className="cursor-pointer">
                  Главная
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>История платежей</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        
        <div className="text-center py-8">
          <p className="text-red-600">Ошибка при загрузке данных: {courierPaymentsData.error}</p>
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Обновить страницу
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <Button 
          variant="ghost" 
          onClick={handleBackToDashboard}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Назад к главной
        </Button>
        
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={handleBackToDashboard} className="cursor-pointer">
                Главная
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>История платежей</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <h1 className="text-2xl font-bold">История платежей</h1>
        <p className="text-muted-foreground">
          Просмотр всех платежей и транзакций
        </p>
      </div>

      <PaymentStatCards 
        totalPaid={courierPaymentsData.totalPaid}
        pendingAmount={courierPaymentsData.pendingAmount}
        lastPaymentAmount={courierPaymentsData.lastPaymentAmount}
        isLoading={courierPaymentsData.isLoading}
      />
      
      <Card>
        <CardHeader>
          <CardTitle>Все платежи</CardTitle>
          <PaymentFilters 
            setStatusFilter={setStatusFilter}
            currentFilter={statusFilter}
          />
        </CardHeader>
        <CardContent>
          <PaymentsTable 
            payments={filteredPayments} 
            loading={courierPaymentsData.isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CourierPayments;
